import type { Slot } from '../../types/assertive'
import { stripAYCode } from './adFunctions'

export type PriceBucket = {
  min: number
  max: number
  increment: number
}

export const priceBuckets: PriceBucket[] = [
  { min: 0, max: 2, increment: 0.05 },
  { min: 2, max: 5, increment: 0.1 },
  { min: 5, max: 10, increment: 0.25 },
  { min: 10, max: 20, increment: 0.5 },
]

export const normalizePricePointBucket = (
  cpm: number | undefined
): string | null => {
  if (!cpm) return null

  const bucket =
    priceBuckets.find((bucket) => cpm >= bucket.min && cpm < bucket.max) || null
  if (!bucket) return '0.00'

  const pricePoint =
    Math.floor((cpm - bucket.min) / bucket.increment) * bucket.increment +
    bucket.min

  return pricePoint.toFixed(2)
}

// if dynamic floor pricing is enabled collect CPM for each placement
export async function getCPMMap(
  isDynamicFloorEnabled: boolean | undefined,
  flooredList: string[],
  getHourlyCpmForPlacement: (placement: string) => Promise<number>
): Promise<Map<string, number>> {
  const cpmMap = new Map<string, number>()
  if (!isDynamicFloorEnabled) return cpmMap
  try {
    const cpmPromises = flooredList.map(async (placement: string) => {
      const cpm = await getHourlyCpmForPlacement(placement)
      return { placement, cpm }
    })
    const results = await Promise.allSettled(cpmPromises)

    results.forEach((result) => {
      if (result.status === 'fulfilled' && result.value.cpm) {
        cpmMap.set(result.value.placement, result.value.cpm)
      } else if (result.status === 'rejected') {
        logger.error('Failed to fetch CPM for placement:', result.reason)
      }
    })
  } catch (error) {
    logger.error('Error fetching CPMs:', error)
  }

  return cpmMap
}

export function updateFloorSlotTargeting(
  adUnitInstanceCode: string,
  slot: Slot,
  cpmMap: Map<string, number>,
  isDynamicFloorEnabled: boolean | undefined
) {
  const placement = stripAYCode(adUnitInstanceCode)

  // If dynamic floor pricing is disabled or this placement has no CPM, only update KV for placement
  if (!isDynamicFloorEnabled || !cpmMap.has(placement)) {
    slot.updateTargetingFromMap({ placement })
    return
  }

  const defaultCPM = Number(useAppConfig().defaultCpm)
  // Otherwise, set the targeting with the price floor.
  slot.updateTargetingFromMap({
    ay_floor:
      normalizePricePointBucket(cpmMap.get(placement) ?? defaultCPM ?? 0) ?? '',
    placement,
  })
  logger.info(
    `Updated slot targeting for ${placement} with ay_floor: ${cpmMap.get(placement)}`
  )
}
